/**
 * Drive Mode playlist state machine (QLearn iOS Audio Mode, contract T-C slice 1).
 *
 * The one genuinely novel moving part of Drive Mode: a pure-JS state machine that
 * runs the per-question audio loop and knows NOTHING about audio engines, React,
 * or the network. Everything it touches is INJECTED:
 *
 *   engine        { play(segmentRef), pause(), resume(), onEnded(cb) }
 *   queue         async iterator — queue.next() -> Promise<{ value, done }>,
 *                 value = { questionId, segments } where `segments` maps each
 *                 playback phase to an ORDERED LIST of segment refs (design-gate
 *                 R4): { stem:[...], options:[...], answer:[...], deeper:[...] }.
 *                 Today each list has one element (the shipped single `options`
 *                 segment); per-option snippet assembly later is a QUEUE-SHAPE
 *                 change only — the player plays every ref in a phase list
 *                 consecutively, so it already handles multi-ref lists.
 *   submitAnswer  async (questionId, optionKey) — the grading seam (mocked here;
 *                 its contract shape is fixed by this slice).
 *   setTimer/clearTimer   injectable timers (default global set/clearTimeout).
 *   now           injectable clock (default Date.now) — reserved for callers.
 *   onState       (state) => void — emitted on every transition.
 *   onReprompt    () => void — the single +20s answer-window chime callback.
 *   config        { advanceMs=3000, repromptMs=20000 }.
 *
 * Design refs: docs/qlearn-audio-ios-design.md §1.2 (answer-window semantics),
 * §1.3 (remote-command matrix — normative, context-dependent), §2.1 (the 8
 * design states) + the issuer's delta-1 ruling adding a 9th state, HOLDING
 * (play/pause during ADVANCE_PAUSE cancels the auto-advance and parks
 * silently; play/pause or next releases; prev replays the just-answered stem;
 * deeper stays valid). Fail-open everywhere: any engine/queue error skips
 * forward, never wedges, never throws to the caller. `submitAnswer` failure
 * still plays the answerBrief and surfaces a `syncPending` flag (grading is
 * server-authoritative later — the design's fail-open rule).
 *
 * DARK: nothing imports this module yet (invariant 6). No flag changes, no UI.
 */

export const STATES = Object.freeze({
  LOADING: "LOADING",
  PLAYING_STEM: "PLAYING_STEM",
  PLAYING_OPTIONS: "PLAYING_OPTIONS",
  AWAITING_ANSWER: "AWAITING_ANSWER",
  PLAYING_ANSWER: "PLAYING_ANSWER",
  PLAYING_DEEPER: "PLAYING_DEEPER",
  ADVANCE_PAUSE: "ADVANCE_PAUSE",
  // Issuer ruling on contract delta 1 (f9:audio-ios-fable): play/pause is the
  // one universal steering-wheel/headset control, so during the auto-advance
  // gap it must do something sensible — it HOLDS: cancels the pending advance
  // timer and parks silently, indefinitely (no timers live in this state).
  // Release: play/pause or next advances to the next question; prev replays
  // the just-answered stem; deeper stays valid (post-answer).
  HOLDING: "HOLDING",
  ENDED: "ENDED",
});

export const REMOTE_COMMANDS = Object.freeze({
  PLAY_PAUSE: "play_pause",
  NEXT: "next",
  PREV: "prev",
});

// Ordered phases of a single question. Each maps to a list of segment refs.
const PHASE = Object.freeze({
  STEM: "stem",
  OPTIONS: "options",
  ANSWER: "answer",
  DEEPER: "deeper",
});

const DEFAULT_CONFIG = Object.freeze({ advanceMs: 3000, repromptMs: 20000 });

/**
 * @param {object} deps see module docstring.
 * @returns {{ start, answer, remoteCommand, deeper, destroy, getState, _send }}
 */
export function createDriveModePlayer(deps) {
  const {
    engine,
    queue,
    submitAnswer,
    onState = () => {},
    onReprompt = () => {},
    now = Date.now,
    setTimer = (cb, ms) => setTimeout(cb, ms),
    clearTimer = (id) => clearTimeout(id),
    config = {},
  } = deps || {};

  const cfg = { ...DEFAULT_CONFIG, ...config };

  // ---- mutable state ------------------------------------------------------
  let state = STATES.LOADING;
  let questionIndex = -1; // 0-based; -1 = not started
  let question = null; // current { questionId, segments }
  let phaseName = null; // one of PHASE.*
  let phaseRefs = []; // ordered segment refs of the current phase
  let phaseIndex = 0; // which ref within the phase is playing
  let paused = false; // in-playback pause toggle
  let pick = null; // the chosen optionKey (answered)
  let answered = false; // did the student answer this question?
  let skipped = false; // did next-track skip an unanswered question?
  let syncPending = false; // submitAnswer failed — grade not yet synced
  let lastError = null; // last fail-open error surfaced
  let repromptTimer = null;
  let advanceTimer = null;
  // Deferred hold (delta-1 ruling, race clause): a play/pause landing in
  // LOADING — e.g. after the advance timer fired but before the next stem
  // plays — is NOT lost. It toggles this flag; when the next segment starts,
  // playback is immediately paused (silence honored, resume on play/pause).
  let pendingHold = false;
  let destroyed = false;
  // Route-loss latch (post-S2 route-safety tranche). Orthogonal to the phase
  // state machine: while held, speech is paused, no new/delayed segment may
  // play, and there is NO automatic resume — only an explicit user play clears
  // it. Idempotent across repeated route-loss events.
  let routeHeld = false;
  // Guards a stale queue pull / submit resolving after we've moved on.
  let loadToken = 0;

  // ---- helpers ------------------------------------------------------------
  function snapshot() {
    return {
      state,
      questionId: question ? question.questionId : null,
      questionIndex,
      phase: phaseName,
      paused,
      routeHeld,
      pick,
      answered,
      skipped,
      syncPending,
      error: lastError,
      done: state === STATES.ENDED,
    };
  }

  function emit() {
    onState(snapshot());
  }

  function clearRepromptTimer() {
    if (repromptTimer !== null) {
      clearTimer(repromptTimer);
      repromptTimer = null;
    }
  }

  function clearAdvanceTimer() {
    if (advanceTimer !== null) {
      clearTimer(advanceTimer);
      advanceTimer = null;
    }
  }

  function clearAllTimers() {
    clearRepromptTimer();
    clearAdvanceTimer();
  }

  function safePlay(ref) {
    // No new speech may start while a route-loss hold is latched.
    if (destroyed || routeHeld) return;
    // Engine failures are fail-open: any throw/rejection becomes an `error`
    // event, which skips the question. The state machine never throws out.
    try {
      const maybePromise = engine.play(ref);
      if (maybePromise && typeof maybePromise.then === "function") {
        maybePromise.catch((e) => send({ type: "error", error: e }));
      }
    } catch (e) {
      send({ type: "error", error: e });
    }
  }

  // ---- phase playback -----------------------------------------------------
  function refsFor(name) {
    const segments = (question && question.segments) || {};
    const list = segments[name];
    return Array.isArray(list) ? list : [];
  }

  function startPhase(name, nextState) {
    phaseName = name;
    phaseRefs = refsFor(name);
    phaseIndex = 0;
    paused = false;
    state = nextState;
    emit();
    playCurrentRef();
  }

  function playCurrentRef() {
    if (phaseIndex >= phaseRefs.length) {
      // Empty (or exhausted) phase — treat as complete, fail-open graceful.
      phaseComplete();
      return;
    }
    safePlay(phaseRefs[phaseIndex]);
    // Deferred hold: a play/pause that landed during LOADING pauses playback
    // the instant it starts (guard: safePlay's sync-error path may have
    // already torn the phase down via loadNext — don't pause a dead phase).
    if (pendingHold && phaseName !== null && !destroyed) {
      pendingHold = false;
      engine.pause();
      paused = true;
      emit();
    }
  }

  function onSegmentEnded() {
    phaseIndex += 1;
    if (phaseIndex < phaseRefs.length) {
      playCurrentRef();
    } else {
      phaseComplete();
    }
  }

  function phaseComplete() {
    switch (phaseName) {
      case PHASE.STEM:
        startPhase(PHASE.OPTIONS, STATES.PLAYING_OPTIONS);
        break;
      case PHASE.OPTIONS:
        enterAwaitingAnswer();
        break;
      case PHASE.ANSWER:
      case PHASE.DEEPER:
        enterAdvancePause();
        break;
      default:
        // Not in a playing phase — nothing to do.
        break;
    }
  }

  // ---- distinct state entries ---------------------------------------------
  function enterAwaitingAnswer() {
    clearAllTimers();
    phaseName = null;
    phaseRefs = [];
    phaseIndex = 0;
    paused = false;
    state = STATES.AWAITING_ANSWER;
    // §1.2: pause indefinitely, one gentle re-prompt at +20s, NO auto-skip.
    repromptTimer = setTimer(() => {
      repromptTimer = null; // single-shot — never reschedule
      if (destroyed || state !== STATES.AWAITING_ANSWER) return;
      onReprompt();
    }, cfg.repromptMs);
    emit();
  }

  function enterAdvancePause() {
    clearAllTimers();
    phaseName = null;
    phaseRefs = [];
    phaseIndex = 0;
    paused = false;
    state = STATES.ADVANCE_PAUSE;
    advanceTimer = setTimer(() => {
      advanceTimer = null;
      if (destroyed || state !== STATES.ADVANCE_PAUSE) return;
      loadNext();
    }, cfg.advanceMs);
    emit();
  }

  // Delta-1 ruling: hold the auto-advance gap. Silent, indefinite — NO timer
  // lives in this state, so nothing can fire out of it.
  function enterHolding() {
    clearAllTimers();
    phaseName = null;
    phaseRefs = [];
    phaseIndex = 0;
    paused = false;
    state = STATES.HOLDING;
    emit();
  }

  function enterEnded() {
    clearAllTimers();
    phaseName = null;
    phaseRefs = [];
    phaseIndex = 0;
    paused = false;
    pendingHold = false; // terminal — a deferred hold has nothing to pause
    state = STATES.ENDED;
    emit();
  }

  // ---- queue advance ------------------------------------------------------
  function loadNext() {
    clearAllTimers();
    // Reset per-question flags.
    pick = null;
    answered = false;
    skipped = false;
    syncPending = false;
    lastError = null;
    phaseName = null;
    phaseRefs = [];
    phaseIndex = 0;
    paused = false;
    state = STATES.LOADING;
    emit();

    const token = ++loadToken;
    Promise.resolve()
      .then(() => queue.next())
      .then((result) => {
        // Stale pull (destroyed, or superseded by a newer advance) — drop it.
        if (destroyed || token !== loadToken) return;
        if (!result || result.done || !result.value) {
          enterEnded();
          return;
        }
        question = result.value;
        questionIndex += 1;
        startPhase(PHASE.STEM, STATES.PLAYING_STEM);
      })
      .catch(() => {
        // Fail-open: the queue is the only source of further questions, so a
        // pull failure ends the session (never wedges, never throws out).
        if (destroyed || token !== loadToken) return;
        enterEnded();
      });
  }

  // Skip forward one question. `markSkipped` iff the current question was
  // never answered (next-track on an unheard question). Advance funnels through
  // loadNext(), so a persistently-throwing queue can only ENDED — never loop.
  function skipToNext() {
    loadNext();
  }

  // ---- remote-command semantics (design §1.3, normative) ------------------
  // play/pause: in-playback = pause/resume toggle; answer-window = replay options.
  // next-track: skip question everywhere (marked skipped iff unanswered).
  // prev-track: replay stem everywhere.
  function togglePause() {
    if (paused) {
      engine.resume();
      paused = false;
    } else {
      engine.pause();
      paused = true;
    }
    emit();
  }

  // ---- route-loss hold (post-S2 route-safety) -----------------------------
  // Latch a no-auto-resume hold that synchronously pauses speech and blocks any
  // pending/new speech. Idempotent: a repeat route-loss event re-pauses (harmless)
  // but does not re-emit or stack.
  function applyRouteHold() {
    if (destroyed) return;
    const wasHeld = routeHeld;
    routeHeld = true;
    if (engine && typeof engine.holdRoute === "function") engine.holdRoute();
    else engine.pause();
    paused = true;
    if (!wasHeld) emit();
  }

  // Cleared ONLY by an explicit user play. Releases the latch exactly once and
  // re-issues the current phase's current segment so resume actually produces
  // audio (the segment in-flight at route-loss was dropped by the engine).
  function clearRouteHoldAndResume() {
    if (!routeHeld) return false;
    routeHeld = false;
    if (engine && typeof engine.releaseRoute === "function") engine.releaseRoute();
    paused = false;
    if (!destroyed && phaseName !== null && phaseIndex < phaseRefs.length) {
      safePlay(phaseRefs[phaseIndex]);
    }
    emit();
    return true;
  }

  function replayStem() {
    clearAllTimers();
    startPhase(PHASE.STEM, STATES.PLAYING_STEM);
  }

  function replayOptions() {
    clearAllTimers();
    startPhase(PHASE.OPTIONS, STATES.PLAYING_OPTIONS);
  }

  function skipCommand({ markSkipped }) {
    if (markSkipped && !answered) {
      skipped = true;
      emit(); // surface the skip before the LOADING reset wipes it
    }
    skipToNext();
  }

  // ---- answer -------------------------------------------------------------
  function doAnswer(optionKey) {
    clearRepromptTimer();
    pick = optionKey;
    answered = true;
    syncPending = false;

    const answeredQid = question ? question.questionId : null;
    // Merge-gate F1: capture the load token at answer time. `question` alone
    // is not a safe staleness guard — loadNext() resets syncPending and enters
    // LOADING but leaves `question` pointing at the answered question until
    // the next pull resolves, so a rejection settling inside that window would
    // pass a questionId check and bleed syncPending onto question N+1. Any
    // advance/skip/destroy bumps loadToken, invalidating the stale rejection.
    const tokenAtAnswer = loadToken;
    // Start answerBrief immediately (design: answerBrief autoplays; grading is
    // server-authoritative later). Fire the grade seam in parallel; a rejection
    // flips syncPending without interrupting playback (fail-open).
    Promise.resolve()
      .then(() => submitAnswer(answeredQid, optionKey))
      .catch(() => {
        if (destroyed || tokenAtAnswer !== loadToken) return;
        // Only surface the flag if we're still on the same question (redundant
        // with the token guard, but harmless belt-and-suspenders).
        if (question && question.questionId === answeredQid) {
          syncPending = true;
          emit();
        }
      });

    startPhase(PHASE.ANSWER, STATES.PLAYING_ANSWER);
  }

  // ---- central dispatch ---------------------------------------------------
  // event: { type: 'segment-ended' } | { type:'answer', key }
  //      | { type:'remote', command } | { type:'deeper' } | { type:'error', error }
  // Per-state event handlers (design §1.3 / §2.1 + delta-1 HOLDING). Behavior is
  // identical to the prior nested switch; it is decomposed into a dispatch table
  // of small named handlers so `send()` stays flat (each handler and send() are
  // well under the cognitive-complexity budget). An event kind absent from a
  // handler is a deliberate no-op for that state (the exhaustive state×event
  // matrix test pins every one of the 63 cells).

  // Route a remote command to its per-state action. Only an OWN, callable table
  // entry runs; every unmapped command — including prototype-chain keys like
  // `__proto__` and inherited function-valued keys (`constructor`, `toString`,
  // `hasOwnProperty`, …) — is an inert no-op, exactly as the pre-dispatch-table
  // strict-equality comparisons were (O3 fix).
  function dispatchRemote(command, actions) {
    if (!Object.prototype.hasOwnProperty.call(actions, command)) return;
    const action = actions[command];
    if (typeof action === "function") action();
  }

  // LOADING: transient async pull. Only play/pause has effect (delta-1 race
  // clause): it toggles a deferred hold so the next segment starts paused; a
  // double-press cancels cleanly. State stays LOADING.
  function handleLoading(event) {
    if (event.type === "remote" && event.command === REMOTE_COMMANDS.PLAY_PAUSE) {
      pendingHold = !pendingHold;
    }
  }

  // PLAYING_STEM / PLAYING_OPTIONS.
  function handlePlayingPhase(event) {
    if (event.type === "segment-ended") { onSegmentEnded(); return; }
    if (event.type === "remote") {
      dispatchRemote(event.command, {
        [REMOTE_COMMANDS.PLAY_PAUSE]: togglePause,
        [REMOTE_COMMANDS.NEXT]: () => skipCommand({ markSkipped: true }),
        [REMOTE_COMMANDS.PREV]: replayStem,
      });
    }
  }

  // AWAITING_ANSWER. §1.3: play/pause = replay options here.
  function handleAwaitingAnswer(event) {
    if (event.type === "answer") { doAnswer(event.key); return; }
    if (event.type === "remote") {
      dispatchRemote(event.command, {
        [REMOTE_COMMANDS.PLAY_PAUSE]: replayOptions,
        [REMOTE_COMMANDS.NEXT]: () => skipCommand({ markSkipped: true }),
        [REMOTE_COMMANDS.PREV]: replayStem,
      });
    }
  }

  // PLAYING_ANSWER / PLAYING_DEEPER. deeper is valid only from PLAYING_ANSWER.
  function handlePlayingAnswerPhase(event) {
    if (event.type === "segment-ended") { onSegmentEnded(); return; }
    if (event.type === "deeper") {
      if (state === STATES.PLAYING_ANSWER) startPhase(PHASE.DEEPER, STATES.PLAYING_DEEPER);
      return;
    }
    if (event.type === "remote") {
      dispatchRemote(event.command, {
        [REMOTE_COMMANDS.PLAY_PAUSE]: togglePause,
        [REMOTE_COMMANDS.NEXT]: () => skipCommand({ markSkipped: false }), // answered — plain advance
        [REMOTE_COMMANDS.PREV]: replayStem,
      });
    }
  }

  // ADVANCE_PAUSE. Delta-1: play/pause HOLDS the auto-advance gap (park silent).
  function handleAdvancePause(event) {
    if (event.type === "deeper") { clearAllTimers(); startPhase(PHASE.DEEPER, STATES.PLAYING_DEEPER); return; }
    if (event.type === "remote") {
      dispatchRemote(event.command, {
        [REMOTE_COMMANDS.PLAY_PAUSE]: enterHolding,
        [REMOTE_COMMANDS.NEXT]: () => skipCommand({ markSkipped: false }),
        [REMOTE_COMMANDS.PREV]: replayStem,
      });
    }
  }

  // HOLDING (delta-1 ruling). Release: play/pause OR next advances (answered —
  // never marked skipped); prev replays the just-answered stem; deeper valid.
  function handleHolding(event) {
    if (event.type === "deeper") { startPhase(PHASE.DEEPER, STATES.PLAYING_DEEPER); return; }
    if (event.type === "remote") {
      const advance = () => skipCommand({ markSkipped: false });
      dispatchRemote(event.command, {
        [REMOTE_COMMANDS.PLAY_PAUSE]: advance,
        [REMOTE_COMMANDS.NEXT]: advance,
        [REMOTE_COMMANDS.PREV]: replayStem,
      });
    }
  }

  const STATE_HANDLERS = {
    [STATES.LOADING]: handleLoading,
    [STATES.PLAYING_STEM]: handlePlayingPhase,
    [STATES.PLAYING_OPTIONS]: handlePlayingPhase,
    [STATES.AWAITING_ANSWER]: handleAwaitingAnswer,
    [STATES.PLAYING_ANSWER]: handlePlayingAnswerPhase,
    [STATES.PLAYING_DEEPER]: handlePlayingAnswerPhase,
    [STATES.ADVANCE_PAUSE]: handleAdvancePause,
    [STATES.HOLDING]: handleHolding,
    [STATES.ENDED]: () => {}, // terminal — everything ignored
  };

  function send(event) {
    if (destroyed) return;
    const { type } = event;

    // Route-loss hold is a hard gate on the event pump, orthogonal to `state`.
    // While latched, the ONLY event that has effect is an explicit user play
    // (play/pause), which clears the hold exactly once and resumes; everything
    // else is deferred so nothing can auto-resume audio.
    if (routeHeld) {
      if (type === "remote" && event.command === REMOTE_COMMANDS.PLAY_PAUSE) {
        clearRouteHoldAndResume();
      }
      return;
    }

    // error() is fail-open in EVERY non-terminal state: surface the error on the
    // current state, then skip to the next question (never wedge, never throw).
    if (type === "error") {
      if (state === STATES.ENDED) return; // terminal — ignore
      lastError = event.error || new Error("drive-mode error");
      emit(); // observable error transition before the LOADING reset clears it
      skipToNext();
      return;
    }

    const handler = STATE_HANDLERS[state];
    if (handler) handler(event);
  }

  // ---- public API ---------------------------------------------------------
  // Wire the engine's ended callback into the machine once.
  if (engine && typeof engine.onEnded === "function") {
    engine.onEnded(() => send({ type: "segment-ended" }));
  }

  return {
    /** Begin the session — pulls the first question and plays its stem. */
    start() {
      if (destroyed) return;
      questionIndex = -1;
      loadNext();
    },
    /** Student picked an option (only honored in AWAITING_ANSWER). */
    answer(optionKey) {
      send({ type: "answer", key: optionKey });
    },
    /** Lock-screen / remote command: 'play_pause' | 'next' | 'prev'. */
    remoteCommand(command) {
      send({ type: "remote", command });
    },
    /** Opt-in deeper explanation (only from PLAYING_ANSWER / ADVANCE_PAUSE / HOLDING). */
    deeper() {
      send({ type: "deeper" });
    },
    /**
     * Route-loss safety: latch a no-auto-resume hold that synchronously pauses
     * speech and blocks pending/new speech. Idempotent; cleared only by an
     * explicit user play (the play/pause command).
     */
    routeHold() {
      applyRouteHold();
    },
    /** Tear down: cancel every pending timer; ignore all further events. */
    destroy() {
      destroyed = true;
      loadToken += 1; // invalidate any in-flight pull/submit
      clearAllTimers();
    },
    /** Current emitted state snapshot. */
    getState() {
      return snapshot();
    },
    // Low-level event injection — the ergonomic methods above funnel through
    // here, and it is the seam the exhaustive state×event matrix test drives.
    _send: send,
  };
}

export default createDriveModePlayer;
